"use client"

import { useState, useEffect, useRef } from "react"
import { X, Download, ChevronLeft, ChevronRight, ShoppingCart, Check, Heart, ImageIcon } from "lucide-react"
import type { Photo } from "@/lib/photos"
import { useCart } from "@/context/cart-context"
import { useSelection } from "@/context/selection-context"
import { useSettings } from "@/components/branding-provider"
import { LightboxImage } from "@/components/lightbox-image"
import { getDownloadUrl } from "@/lib/download-url"
import { getLightboxPreviewUrl, getLightboxPreviewWidth, getPhotoDownloadVersion, getWatermarkedDownloadUrl } from "@/lib/lightbox-image"
import { formatPrintProductLabel, getPrintFitStatus, isPrintProductSafeForPhoto } from "@/lib/print-catalog"
import { trackStoreEvent } from "@/components/store-analytics-tracker"

const MAX_PRELOADED_IMAGES = 6

interface ImageModalProps {
  photo: Photo
  onClose: () => void
  onNext?: () => void
  onPrev?: () => void
  hasNext?: boolean
  hasPrev?: boolean
  ecommerceEnabled?: boolean
  favoritesEnabled?: boolean
  freeDownloadMaxWidth?: number
  downloadVersion?: string
  freeDownloadsEnabled?: boolean
  highResDownloadsEnabled?: boolean
  highResDownloadMode?: 'watermarked' | 'original'
  shareToken?: string
  /** Saved favorites-list token that opens this private photo. */
  favoriteToken?: string
  contentMode?: "sports-public" | "sports-private" | "travel-portfolio"
  currentIndex?: number
  totalCount?: number
  /** Exact lightbox URLs of the neighbouring photos, preloaded once this one is showing. */
  preloadUrls?: string[]
}

export function ImageModal({ photo, onClose, onNext, onPrev, hasNext, hasPrev, ecommerceEnabled = true, favoritesEnabled = true, freeDownloadMaxWidth = 1200, downloadVersion = 'default', freeDownloadsEnabled = true, highResDownloadsEnabled = false, highResDownloadMode = 'original', shareToken, favoriteToken, contentMode = "sports-public", currentIndex, totalCount, preloadUrls }: ImageModalProps) {
  const [loadedPreviewUrl, setLoadedPreviewUrl] = useState<string | null>(null)
  const [selectedPrintProductId, setSelectedPrintProductId] = useState('')
  const [printQuantity, setPrintQuantity] = useState(1)
  const settings = useSettings()
  const { addToCart, addPrintToCart, isInCart, isPrintInCart } = useCart()
  const { toggleSelection, isSelected } = useSelection()
  const inCart = isInCart(photo.id)
  const selected = isSelected(photo.id)
  const allActivePrintProducts = settings.commerce?.prints?.enabled
    ? settings.commerce.prints.catalog.filter(product => product.active)
    : []
  const activePrintProducts = allActivePrintProducts.filter(product => isPrintProductSafeForPhoto(photo, product))
  const hiddenCropProductCount = allActivePrintProducts.length - activePrintProducts.length
  const selectedPrintProduct = activePrintProducts.find(product => product.id === selectedPrintProductId) ?? activePrintProducts[0] ?? null
  const printInCart = selectedPrintProduct ? isPrintInCart(photo.id, selectedPrintProduct.id) : false
  const touchStartX = useRef(0)
  const touchStartY = useRef(0)
  const highResPrice = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format((settings.commerce?.highResPriceCents ?? 1000) / 100)
  const modalSupportText = settings.commerce?.showModalSupportText
    ? contentMode === "travel-portfolio"
      ? "High-resolution files are available for personal prints and display."
      : settings.commerce.salesMessage.trim()
    : ''
  const lightboxOptions = {
    freeDownloadsEnabled,
    highResDownloadsEnabled,
    highResDownloadMode,
    freeDownloadMaxWidth,
    downloadVersion,
    shareToken,
    favoriteToken,
  }
  const photoDownloadVersion = getPhotoDownloadVersion(photo, downloadVersion)
  const previewUrl = getLightboxPreviewUrl(photo, lightboxOptions)
  const previewMaxWidth = getLightboxPreviewWidth(lightboxOptions)
  const preloadKey = (preloadUrls ?? []).join('\n')
  const actionButtonClass = "flex min-h-11 items-center justify-center gap-2 rounded-full border border-white/15 px-4 text-sm font-semibold shadow-sm backdrop-blur-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
  const iconButtonClass = "flex min-h-11 min-w-11 items-center justify-center rounded-full border border-white/15 bg-white/10 text-white shadow-sm backdrop-blur-md transition-colors hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
  const printPrice = selectedPrintProduct
    ? formatCurrency(selectedPrintProduct.priceCents * printQuantity)
    : null
  const printFitStatus = selectedPrintProduct ? getPrintFitStatus(photo, selectedPrintProduct) : null

  useEffect(() => {
    trackStoreEvent({
      eventType: 'photo_open',
      galleryPath: getGalleryPathFromFilename(photo.filename),
      filename: photo.filename,
      metadata: {
        width: photo.width,
        height: photo.height,
      },
    })
  }, [photo.filename, photo.height, photo.width])

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose()
      } else if (e.key === "ArrowRight" && onNext) {
        onNext()
      } else if (e.key === "ArrowLeft" && onPrev) {
        onPrev()
      }
    }
    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [onClose, onNext, onPrev])

  // Once this photo is on screen, warm the browser cache for its neighbours using the
  // exact URLs the lightbox will request, so next/previous open instantly.
  const preloadedImages = useRef(new Map<string, HTMLImageElement>())
  useEffect(() => {
    if (loadedPreviewUrl !== previewUrl || !preloadKey) return
    const preloaded = preloadedImages.current
    for (const url of preloadKey.split('\n')) {
      if (preloaded.has(url)) continue
      const img = new Image()
      img.decoding = 'async'
      img.fetchPriority = 'low'
      img.src = url
      preloaded.set(url, img)
    }
    while (preloaded.size > MAX_PRELOADED_IMAGES) {
      const oldest = preloaded.keys().next().value
      if (oldest === undefined) break
      preloaded.delete(oldest)
    }
  }, [loadedPreviewUrl, previewUrl, preloadKey])

  // Handle touch swipe navigation
  useEffect(() => {
    const handleTouchStart = (e: Event) => {
      const touchEvent = e as TouchEvent
      touchStartX.current = touchEvent.touches[0].clientX
      touchStartY.current = touchEvent.touches[0].clientY
    }

    const handleTouchEnd = (e: Event) => {
      const touchEvent = e as TouchEvent
      const touchEndX = touchEvent.changedTouches[0].clientX
      const touchEndY = touchEvent.changedTouches[0].clientY

      const deltaX = touchEndX - touchStartX.current
      const deltaY = touchEndY - touchStartY.current

      // Only trigger if horizontal swipe is more significant than vertical scroll
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
        if (deltaX > 0 && onPrev && hasPrev) {
          // Swiped right -> show previous
          onPrev()
        } else if (deltaX < 0 && onNext && hasNext) {
          // Swiped left -> show next
          onNext()
        }
      }
    }

    const modalElement = document.querySelector('[data-modal="image-container"]')
    if (modalElement) {
      modalElement.addEventListener("touchstart", handleTouchStart, false)
      modalElement.addEventListener("touchend", handleTouchEnd, false)

      return () => {
        modalElement.removeEventListener("touchstart", handleTouchStart)
        modalElement.removeEventListener("touchend", handleTouchEnd)
      }
    }
  }, [onNext, onPrev, hasNext, hasPrev])

  // Prevent scrolling when modal is open
  useEffect(() => {
    const originalStyle = window.getComputedStyle(document.body).overflow
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth

    document.body.style.overflow = "hidden"
    document.body.style.paddingRight = `${scrollbarWidth}px`

    return () => {
      document.body.style.overflow = originalStyle
      document.body.style.paddingRight = "0px"
    }
  }, [])

  const handleDownload = () => {
    if (!freeDownloadsEnabled) return
    const anchor = document.createElement("a")
    anchor.href = getWatermarkedDownloadUrl(photo, lightboxOptions)
    anchor.download = `watermarked-${photo.filename.split('/').pop() || 'photo.jpg'}`
    anchor.style.display = "none"
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
  }

  const handleHighResDownload = () => {
    const params = {
      variant: 'original',
      v: photoDownloadVersion,
      share: shareToken,
      fav: favoriteToken,
    }

    const anchor = document.createElement("a")
    anchor.href = getDownloadUrl(photo.filename, params)
    anchor.download = `${highResDownloadMode === 'watermarked' ? 'watermarked-high-res' : 'original'}-${photo.filename.split('/').pop() || 'photo.jpg'}`
    anchor.style.display = "none"
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Viewing ${photo.filename.split('/').pop() || 'photo'}`}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div className="absolute left-4 right-4 top-4 z-50 flex flex-wrap justify-end gap-2">
        {favoritesEnabled && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleSelection(photo, { shareToken });
            }}
            className={`${actionButtonClass} ${selected
              ? "bg-white text-red-500"
              : "bg-white/10 text-white hover:bg-white/20"
              }`}
          >
            <Heart className={`h-5 w-5 ${selected ? "fill-current" : ""}`} />
            <span className="hidden text-sm font-medium sm:inline">
              {selected ? "Selected" : "Select"}
            </span>
          </button>
        )}
        {ecommerceEnabled && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (!inCart) addToCart(photo);
            }}
            className={`${actionButtonClass} ${inCart
              ? "cursor-default bg-emerald-500 text-white"
              : "bg-white text-black hover:bg-white/90"
              }`}
          >
            {inCart ? <Check className="h-5 w-5" /> : <ShoppingCart className="h-5 w-5" />}
            <span className="text-sm font-medium">
              {inCart ? "Digital in cart" : `Add digital (${highResPrice})`}
            </span>
          </button>
        )}
        {ecommerceEnabled && selectedPrintProduct && (
          <div
            className="flex min-h-11 flex-wrap items-center gap-2 rounded-full border border-white/15 bg-white/10 p-1 shadow-sm backdrop-blur-md"
            onClick={(event) => event.stopPropagation()}
          >
            <select
              value={selectedPrintProduct.id}
              onChange={(event) => setSelectedPrintProductId(event.target.value)}
              className="h-9 max-w-36 rounded-full border border-white/15 bg-black/35 px-3 text-sm font-semibold text-white outline-none focus-visible:ring-2 focus-visible:ring-white/70"
              aria-label="Choose print size"
            >
              {activePrintProducts.map(product => (
                <option key={product.id} value={product.id} className="bg-neutral-950 text-white">
                  {formatPrintProductLabel(product)}
                </option>
              ))}
            </select>
            <div className="flex items-center overflow-hidden rounded-full border border-white/15 bg-black/35">
              <button
                type="button"
                onClick={() => setPrintQuantity(quantity => Math.max(1, quantity - 1))}
                className="h-9 w-9 text-white/80 transition-colors hover:bg-white/15 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
                aria-label="Decrease print quantity"
              >
                -
              </button>
              <span className="min-w-8 text-center text-sm font-semibold text-white">{printQuantity}</span>
              <button
                type="button"
                onClick={() => setPrintQuantity(quantity => Math.min(selectedPrintProduct.maxQuantity, quantity + 1))}
                className="h-9 w-9 text-white/80 transition-colors hover:bg-white/15 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
                aria-label="Increase print quantity"
              >
                +
              </button>
            </div>
            <button
              type="button"
              onClick={() => addPrintToCart(photo, selectedPrintProduct.id, printQuantity)}
              className={`${actionButtonClass} ${printInCart ? "bg-amber-500 text-white" : "bg-white text-black hover:bg-white/90"}`}
            >
              {printInCart ? <Check className="h-5 w-5" /> : <ImageIcon className="h-5 w-5" />}
              <span className="text-sm font-medium">
                {printInCart ? "Print in cart" : `Add print (${printPrice})`}
              </span>
            </button>
          </div>
        )}
        {ecommerceEnabled && hiddenCropProductCount > 0 && (
          <div
            className="max-w-sm rounded-2xl border border-amber-400/25 bg-amber-500/15 px-4 py-3 text-xs leading-5 text-amber-50 shadow-sm backdrop-blur-md"
            onClick={(event) => event.stopPropagation()}
          >
            {hiddenCropProductCount} print {hiddenCropProductCount === 1 ? 'size is' : 'sizes are'} unavailable for this photo because cropping would be required.
          </div>
        )}
        {highResDownloadsEnabled && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleHighResDownload();
            }}
            className={`${actionButtonClass} cursor-pointer bg-white/10 text-white hover:bg-white/20`}
          >
            <Download className="h-4 w-4" />
            {highResDownloadMode === 'watermarked' ? 'High-res watermarked' : 'Original'}
          </button>
        )}
        {freeDownloadsEnabled && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDownload();
            }}
            className={`${actionButtonClass} cursor-pointer bg-white/10 text-white hover:bg-white/20`}
          >
            <Download className="h-4 w-4" />
            Social download
          </button>
        )}
        {/* TODO Add setting for different style of download buttons */}
        {/* <button
          onClick={(e) => {
            e.stopPropagation();
            handleDownload();
          }}
          className="rounded-full bg-white/10 p-3 text-white hover:bg-white/20 focus:outline-none"
          title="Download Watermarked Preview"
        >
          <Download className="h-6 w-6" />
          <span className="sr-only">Download</span>
        </button> */}
        <button
          onClick={onClose}
          className={`${iconButtonClass} cursor-pointer`}
        >
          <X className="h-6 w-6" />
          <span className="sr-only">Close</span>
        </button>
      </div>

      {hasPrev && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            onPrev?.()
          }}
          className="absolute left-4 top-1/2 z-50 hidden min-h-12 min-w-12 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-white/10 text-white shadow-sm backdrop-blur-md transition-colors hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 lg:flex"
        >
          <ChevronLeft className="h-8 w-8" />
          <span className="sr-only">Previous</span>
        </button>
      )}

      {hasNext && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            onNext?.()
          }}
          className="absolute right-4 top-1/2 z-50 hidden min-h-12 min-w-12 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-white/10 text-white shadow-sm backdrop-blur-md transition-colors hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 lg:flex"
        >
          <ChevronRight className="h-8 w-8" />
          <span className="sr-only">Next</span>
        </button>
      )}

      <div
        className="relative flex max-h-[90vh] max-w-[92vw] items-center justify-center overflow-hidden rounded-2xl bg-black shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-modal="image-container"
      >
        <LightboxImage
          key={previewUrl}
          photo={photo}
          previewUrl={previewUrl}
          previewMaxWidth={previewMaxWidth}
          onPreviewLoad={() => setLoadedPreviewUrl(previewUrl)}
        />

        <div className="absolute bottom-0 left-0 right-0 z-10 bg-gradient-to-t from-black/80 to-transparent p-6 text-white">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div className="flex flex-col gap-1">
              {currentIndex && totalCount && (
                <p className="text-xs font-semibold uppercase tracking-wide text-white/70">
                  Photo {currentIndex} of {totalCount}
                </p>
              )}
              <p className="text-sm font-medium">{new Date(photo.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' })}</p>
              <p className="text-xs text-gray-300">{photo.filename.split('/').pop()}</p>
            </div>
            {(modalSupportText || printFitStatus) && (
              <div className="max-w-sm text-xs leading-5 text-white/75 sm:text-right">
                {selectedPrintProduct && printFitStatus ? (
                  <span className="mb-1 block font-medium text-white/85">{printFitStatus.message}</span>
                ) : null}
                {modalSupportText ? <span>{modalSupportText}</span> : null}
              </div>
            )}
          </div>
        </div>
      </div >
    </div >
  )
}

function formatCurrency(cents: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100)
}

function getGalleryPathFromFilename(filename: string) {
  const parts = filename.split('/').filter(Boolean)
  return parts.length > 1 ? parts.slice(0, -1).join('/') : null
}
